import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { io } from "socket.io-client";
import Navbar from "../NavBar";
import NewSidebar from "../newSideBar";
import UserDetails from "./UserDetails";
import InboxPanel from "./InboxPanel";
import "./inbox.css";

const CHAT_STATUS = {
  SOLVED: 3,
  MY_OPEN: 2,
};

const INACTIVITY_THRESHOLD = 5 * 60 * 1000;

const SOCKET_URL = process.env.REACT_APP_CHAT_BACKEND_URL;
const socket = io(SOCKET_URL, {
  transports: ["websocket"],
  reconnection: true,
});

// ========== Enhanced useChatData with Unread Counts ==========
// ========== Fixed useChatData ==========
const useChatData = (status) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const localURL = localStorage.getItem("shopURL");

  // Move headers outside useCallback to avoid dependency issues
  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      "x-shop-url": localURL,
    }),
    [localURL]
  );

  // ✅ FIX: Use useCallback with proper dependencies
  const fetchCollections = useCallback(async () => {
    console.log("🔄 fetchCollections called for status:", status);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/email-list/${status}`,
        { headers }
      );
      const users = response.data.users || [];
      setCollections(users);

      // Fetch unread counts for each user
      const unreadPromises = users.map(async (user) => {
        try {
          const unreadRes = await axios.get(
            `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/unread-count/${user.email}`,
            { headers }
          );
          return { email: user.email, count: unreadRes.data.count || 0 };
        } catch (err) {
          console.error(`Error fetching unread count for ${user.email}:`, err);
          return { email: user.email, count: 0 };
        }
      });

      const unreadResults = await Promise.all(unreadPromises);
      const unreadMap = unreadResults.reduce((acc, item) => {
        acc[item.email] = item.count;
        return acc;
      }, {});

      console.log("📊 Unread counts:", unreadMap);
      setUnreadCounts(unreadMap);
    } catch (error) {
      console.error("❌ Error fetching collections:", error);
      setCollections([]);
      setUnreadCounts({});
    } finally {
      setLoading(false);
    }
  }, [status, headers]); // ✅ Only depend on status and headers

  // ✅ FIX: Only call fetchCollections when status changes
  useEffect(() => {
    console.log("🎯 useEffect in useChatData - status changed:", status);
    fetchCollections();
  }, [fetchCollections]); // ✅ Only depend on fetchCollections

  return {
    collections,
    loading,
    unreadCounts,
    refetch: fetchCollections,
  };
};

// ========== Enhanced Scroll Management ==========
const useScrollManagement = () => {
  const chatContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
  }, []);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: behavior,
      });
      setIsAtBottom(true);
    }
  }, []);

  const scrollToBottomIfNeeded = useCallback(() => {
    if (isAtBottom && chatContainerRef.current) {
      scrollToBottom("smooth");
    }
  }, [isAtBottom, scrollToBottom]);

  return {
    chatContainerRef,
    handleScroll,
    scrollToBottom,
    scrollToBottomIfNeeded,
    isAtBottom,
  };
};

// ========== Enhanced Chat Messages Hook ==========
const useChatMessages = (
  socket,
  selectedCollection,
  scrollToBottomIfNeeded
) => {
  const [messages, setMessages] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  // Scroll when typing starts
  useEffect(() => {
    if (isTyping) {
      setTimeout(() => {
        scrollToBottomIfNeeded();
      }, 100);
    }
  }, [isTyping, scrollToBottomIfNeeded]);

  useEffect(() => {
    if (!socket || !selectedCollection) {
      console.log("❌ useChatMessages - Missing socket or selectedCollection");
      return;
    }

    const handleLoadMessages = (payload) => {
      if (payload?.email === selectedCollection) {
        // Ensure each message has read status
        const messagesWithReadStatus = (payload.messages || []).map((msg) => ({
          ...msg,
          read: msg.read || msg.raw?.read || false,
        }));

        setMessages(messagesWithReadStatus);
        setUserDetails(payload.userInfo || {});
        setMessagesLoading(false);

        // ✅ Mark messages as read when loaded
        socket.emit("markAsRead", {
          email: selectedCollection,
          role: "admin",
        });

        // Scroll to bottom after messages load
        setTimeout(() => {
          scrollToBottomIfNeeded();
        }, 100);
      }
    };

    const handleReceiveMessage = (payload) => {
      if (payload?.email === selectedCollection) {
        // New messages should start as unread
        const newMessage = {
          _id: payload.raw?._id || payload.time,
          text: payload.message,
          admin: payload.admin,
          time: payload.time,
          read: payload.read || payload.raw?.read || false,
          raw: payload.raw,
        };

        setMessages((prev) => [...prev, newMessage]);

        // ✅ Auto mark as read when new message received in open chat
        socket.emit("markAsRead", {
          email: selectedCollection,
          role: "admin",
        });

        // Scroll to bottom when new message arrives
        setTimeout(() => {
          scrollToBottomIfNeeded();
        }, 100);
      }
    };

    const handleTypingStatus = ({ email, role, isTyping: typing }) => {
      if (email === selectedCollection && role === "user") {
        setIsTyping(typing);

        // Scroll to bottom when user starts typing
        if (typing) {
          setTimeout(() => {
            scrollToBottomIfNeeded();
          }, 100);
        }
      }
    };

    const handleMessageSeen = (data) => {
      console.log("👁️ Message seen update:", data);
      // Update the specific message's read status
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId || msg.time === data.time
            ? { ...msg, read: true }
            : msg
        )
      );
    };

    const handleAllMessagesSeen = (data) => {
      console.log("✅ All messages read by:", data.role);
      if (data.role === "admin") {
        // Update unread count when admin reads messages
      }
    };

    // Load messages
    setMessagesLoading(true);
    socket.emit("joinConversation", { email: selectedCollection });

    socket.on("loadMessages", handleLoadMessages);
    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("typingStatus", handleTypingStatus);
    socket.on("messageSeen", handleMessageSeen);
    socket.on("messagesRead", handleAllMessagesSeen);

    const timeoutId = setTimeout(() => {
      setMessagesLoading(false);
    }, 10000);

    return () => {
      socket.off("loadMessages", handleLoadMessages);
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("typingStatus", handleTypingStatus);
      socket.off("messageSeen", handleMessageSeen);
      socket.off("messagesRead", handleAllMessagesSeen);
      clearTimeout(timeoutId);
    };
  }, [socket?.connected, selectedCollection]);

  return {
    messages,
    setMessages,
    userDetails,
    messagesLoading,
    setMessagesLoading,
    isTyping,
  };
};

const formatDateOnly = (dateString) => {
  const [datePart] = dateString.split(" ");
  const [day, month, year] = datePart.split("-").map(Number);
  return `${day.toString().padStart(2, "0")}-${month
    .toString()
    .padStart(2, "0")}-${year}`;
};

const formatTimeOnly = (timestamp) => {
  const parts = timestamp.split(" ");
  const timeParts = parts[1].split(":");
  return `${timeParts[0]}:${timeParts[1]} ${parts[2]}`;
};

const isUserActive = (user) => {
  if (!user?.activeTime) return false;
  return (
    new Date(user.activeTime).getTime() >= Date.now() - INACTIVITY_THRESHOLD
  );
};

// ========== Enhanced SolvedUsersList with Unread Counts ==========
const SolvedUsersList = ({
  collections,
  selectedEmail,
  tenantName,
  onCollectionClick,
  loadingCollection,
  unreadCounts,
}) => {
  if (collections.length === 0) {
    return (
      <div className="empty-state">
        <i className="mdi mdi-check-all"></i>
        <p>No solved conversations</p>
      </div>
    );
  }
  console.log("loadingCollection", loadingCollection);

  return collections.map((user) => {
    const isSelected = selectedEmail === user.email;
    const isLoading = loadingCollection === user.email;
    const isActive = isUserActive(user);
    const unreadCount = unreadCounts[user.email] || 0;
    console.log(unreadCounts, user.email, unreadCount, isLoading);

    return (
      <Link
        key={user.email}
        to={`/${tenantName}/chat/Conversation/Solved/${user.email}`}
        className={`user-item ${isSelected ? "selected" : ""} ${
          isLoading ? "loading" : ""
        } ${unreadCount > 0 ? "has-unread" : ""}`}
        onClick={() => onCollectionClick(user.email)}
      >
        <div className="user-avatar-container">
          <div className={`user-avatar ${isActive ? "active" : ""}`}>
            {user.email.charAt(0).toUpperCase()}
            {/* Active Status Badge */}
            {isActive && (
              <div className="user-active-indicator">
                <div className="active-pulse"></div>
              </div>
            )}
          </div>
          {/* Loading Spinner */}
          {isLoading && (
            <div className="user-loading-overlay">
              <div className="loading-spinner">
                <div className="spinner-circle"></div>
              </div>
            </div>
          )}
        </div>
        <div className="user-info">
          <div className="user-header">
            <div className="user-identity">
              <span className="user-email">{user.email}</span>
              {isActive && (
                <span className="user-active-badge">
                  <span className="active-dot"></span>
                  Active now
                </span>
              )}
            </div>
          </div>
          {/* Enhanced Unread Badge */}
          {unreadCount > 0 && (
            <div className="unread-indicator">
              <span className="unread-count">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            </div>
          )}
        </div>
        <i className="mdi mdi-chevron-right user-arrow"></i>
      </Link>
    );
  });
};

// ========== Enhanced ChatMessages with Seen Status ==========
const ChatMessages = ({ messages, loading, isTyping }) => {
  if (loading) {
    return (
      <div className="chat-loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="empty-chat-state">
        <i className="mdi mdi-check-all"></i>
        <p>This conversation was resolved</p>
        <small>Marked as solved</small>
      </div>
    );
  }

  return (
    <>
      {messages.map((msg, index) => {
        const isDateChanged =
          index === 0 ||
          formatDateOnly(msg.time) !==
            formatDateOnly(messages[index - 1]?.time);

        // ⭐ Check if message is read (admin messages that user has seen)
        const isRead = msg.read || msg.raw?.read || false;
        const messageKey = msg._id || `${msg.time}-${index}`;

        return (
          <React.Fragment key={messageKey}>
            {isDateChanged && (
              <div className="chat-date-divider">
                <span>{formatDateOnly(msg.time)}</span>
              </div>
            )}
            <div
              className={`message-container ${
                msg.admin ? "outgoing" : "incoming"
              }`}
            >
              <div className={`message-bubble ${msg.likeStatus || ""}`}>
                <div className="message-content">{msg.text}</div>
                <div className="message-meta">
                  <span className="message-time">
                    {formatTimeOnly(msg.time)}
                  </span>
                  {/* ⭐ ADD SEEN STATUS FOR ADMIN MESSAGES */}
                  {msg.admin && (
                    <span
                      className={`message-seen-status ${
                        isRead ? "seen" : "delivered"
                      }`}
                    >
                      {isRead ? "✓✓" : "✓"}
                    </span>
                  )}
                  {msg.admin && msg.likeStatus && (
                    <span className="message-reaction">
                      {msg.likeStatus === "liked" ? (
                        <i className="mdi mdi-thumb-up text-success"></i>
                      ) : (
                        <i className="mdi mdi-thumb-down text-danger"></i>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* Typing Indicator */}
      {isTyping && (
        <div className="typing-indicator">
          <div className="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span>User is typing...</span>
        </div>
      )}
    </>
  );
};

const ChatActionBar = ({
  selectedCollection,
  onReopenConversation,
  loading,
}) => {
  if (!selectedCollection || loading) return null;

  return (
    <div className="chat-action-bar">
      <button
        className="reopen-conversation-btn"
        onClick={onReopenConversation}
      >
        <i className="mdi mdi-archive-arrow-up"></i>
        Reopen Conversation
      </button>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="loading-container">
    <Spinner
      animation="border"
      variant="primary"
      style={{ width: "3rem", height: "3rem" }}
    />
  </div>
);

// ========== Main Solved Component ==========
function SolvedNew() {
  const navigate = useNavigate();
  const { collection } = useParams();
  const tenantName = localStorage.getItem("tenantName");
  const shopUrl = localStorage.getItem("shopURL");

  const [selectedCollection, setSelectedCollection] = useState("");
  const [loadingCollection, setLoadingCollection] = useState("");

  console.log("🔍 SolvedNew - selectedCollection:", selectedCollection);
  // Enhanced hooks with all features
  const {
    collections,
    loading: chatLoading,
    unreadCounts,
  } = useChatData(CHAT_STATUS.SOLVED);

  // Enhanced scroll management
  const { chatContainerRef, handleScroll, scrollToBottomIfNeeded } =
    useScrollManagement();

  // Enhanced chat messages with all features
  const {
    messages,
    userDetails,
    messagesLoading,
    setMessagesLoading,
    isTyping,
  } = useChatMessages(socket, selectedCollection, scrollToBottomIfNeeded);

  // Socket setup
  useEffect(() => {
    if (!shopUrl) return;

    socket.emit("registerShop", shopUrl);

    socket.on("registeredShop", (data) => {
      console.log("✅ Shop registered:", data.shopUrl);
    });

    socket.on("socketError", (err) => {
      console.error("⚠️ Socket error:", err);
    });

    return () => {
      socket.off("registeredShop");
      socket.off("socketError");
    };
  }, [shopUrl]);

  const handleCollectionClick = useCallback(
    (collection) => {
      setSelectedCollection(collection);
      setMessagesLoading(true);
      setLoadingCollection(collection);
    },
    [selectedCollection]
  );

  const handleStatusChange = useCallback(async () => {
    if (!selectedCollection) return;

    try {
      const localURL = localStorage.getItem("shopURL");
      const headers = {
        "Content-Type": "application/json",
        "x-shop-url": localURL,
      };

      await axios.post(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/status-update`,
        { email: selectedCollection, status: CHAT_STATUS.MY_OPEN },
        { headers }
      );

      navigate(`/${tenantName}/chat/Conversation/MyOpen/${selectedCollection}`);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }, [selectedCollection, tenantName, navigate]);

  // Initial selection from URL
  useEffect(() => {
    if (
      collection &&
      collection !== ":collection" &&
      collection !== selectedCollection
    ) {
      setSelectedCollection(collection);
      setMessagesLoading(true);
      setLoadingCollection(collection);
    }
  }, [collection, selectedCollection]);

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, scrollToBottomIfNeeded]);

  // Clear loading state when messages are loaded
  useEffect(() => {
    if (!messagesLoading && loadingCollection) {
      setLoadingCollection("");
    }
  }, [messagesLoading, loadingCollection]);

  if (chatLoading) {
    return (
      <div className="d-flex flex-column w-100">
        <Navbar />
        <div className="d-flex w-100">
          <NewSidebar />
          <div className="main-panel" style={{ overflowY: "auto" }}>
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column w-100">
      <Navbar />
      <div className="d-flex w-100">
        <NewSidebar />
        <div className="main-panel" style={{ overflowY: "auto" }}>
          <div className="content-wrapper">
            <div className="d-xl-flex justify-content-between align-items-start">
              <h2 className="text-dark font-weight-bold mb-2">Conversation</h2>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="tab-content tab-transparent-content">
                  <div
                    className="tab-pane fade show active"
                    id="business-1"
                    role="tabpanel"
                  >
                    <div className="row">
                      <div className="col-12 grid-margin">
                        <div className="card">
                          <div className="card-body">
                            <div className="row d-flex flex-wrap">
                              <InboxPanel />

                              <div className="solved-users-panel col-md-3">
                                <div className="panel-header">
                                  <h3 className="panel-title">
                                    Solved Conversations
                                  </h3>
                                  <div className="panel-badge">
                                    {collections.length}
                                  </div>
                                </div>
                                <div className="users-list-container">
                                  <SolvedUsersList
                                    collections={collections}
                                    selectedEmail={selectedCollection}
                                    tenantName={tenantName}
                                    onCollectionClick={handleCollectionClick}
                                    loadingCollection={loadingCollection}
                                    unreadCounts={unreadCounts}
                                  />
                                </div>
                              </div>

                              <div className="solved-chat-panel col-md-4">
                                <div className="chat-panel-header">
                                  <h3 className="chat-title">
                                    Solved Conversation
                                  </h3>
                                  <div className="chat-status-badge">
                                    Completed
                                  </div>
                                </div>
                                <div
                                  className="chat-messages-container"
                                  ref={chatContainerRef}
                                  onScroll={handleScroll}
                                >
                                  <ChatMessages
                                    messages={messages}
                                    loading={messagesLoading}
                                    isTyping={isTyping}
                                  />
                                </div>
                                <ChatActionBar
                                  selectedCollection={selectedCollection}
                                  onReopenConversation={handleStatusChange}
                                  loading={messagesLoading}
                                />
                              </div>

                              <div className="col-md-3 col-lg-3">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                  <h4 className="card-title mb-0">
                                    Information
                                  </h4>
                                </div>
                                <div className="info-panel">
                                  <UserDetails {...userDetails} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SolvedNew;

// import React, { useEffect, useRef, useState, useCallback } from "react";
// import { Link, useNavigate, useParams } from "react-router-dom";
// import { DateTime } from "luxon";
// import axios from "axios";
// import { Spinner } from "react-bootstrap";
// import Navbar from "../NavBar";
// import NewSidebar from "../newSideBar";
// import UserDetails from "./UserDetails";
// import InboxPanel from "./InboxPanel";
// import "./inbox.css";

// // Constants
// const CHAT_STATUS = {
//   SOLVED: 3,
//   MY_OPEN: 2,
// };

// const INACTIVITY_THRESHOLD = 5 * 60 * 1000;

// // Custom Hooks
// const useChatData = (status) => {
//   const [collections, setCollections] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const localURL = localStorage.getItem("shopURL");

//   const headers = {
//     "Content-Type": "application/json",
//     "x-shop-url": localURL,
//   };

//   const fetchCollections = useCallback(async () => {
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/email-list/${status}`,
//         { headers }
//       );
//       setCollections(response.data.users || []);
//     } catch (error) {
//       console.error("Error fetching collections:", error);
//       setCollections([]);
//     } finally {
//       setLoading(false);
//     }
//   }, [headers, status]);

//   useEffect(() => {
//     fetchCollections();
//   }, [fetchCollections]);

//   return { collections, loading };
// };

// const useChatMessages = (selectedCollection) => {
//   const [messages, setMessages] = useState([]);
//   const [userDetails, setUserDetails] = useState({
//     email: "",
//     system: "",
//     ip: "",
//     location: "",
//   });

//   const localURL = localStorage.getItem("shopURL");
//   const headers = {
//     "Content-Type": "application/json",
//     "x-shop-url": localURL,
//   };

//   const fetchMessages = useCallback(
//     async (collection) => {
//       if (!collection) return;

//       try {
//         const [chatResponse, adminResponse] = await Promise.all([
//           axios.get(
//             `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/email-click/${collection}`,
//             { headers }
//           ),
//           axios.get(
//             `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/messages-get/${collection}`,
//             { headers }
//           ),
//         ]);

//         const { info, user_message } = chatResponse.data;
//         const admin_message = adminResponse.data;

//         const allMessages = [...user_message, ...admin_message].sort((a, b) => {
//           const timeA = DateTime.fromFormat(
//             a.time,
//             "dd-MM-yyyy hh:mm:ss.SSS a"
//           );
//           const timeB = DateTime.fromFormat(
//             b.time,
//             "dd-MM-yyyy hh:mm:ss.SSS a"
//           );
//           return timeA - timeB;
//         });

//         const formattedMessages = allMessages.map((msg) => ({
//           text: msg.message,
//           admin: !!msg.Admin_id,
//           time: msg.time,
//           likeStatus: msg.likeStatus || null,
//         }));

//         setMessages(formattedMessages);
//         setUserDetails({
//           email: info?.email || "",
//           system: info?.system || "",
//           ip: info?.ip || "",
//           location: info?.location || "",
//         });
//       } catch (error) {
//         console.error(`Error fetching messages for ${collection}:`, error);
//         setMessages([]);
//       }
//     },
//     [headers]
//   );

//   return { messages, userDetails, fetchMessages };
// };

// const useScrollManagement = () => {
//   const chatContainerRef = useRef(null);
//   const [isAtBottom, setIsAtBottom] = useState(true);

//   const handleScroll = useCallback(() => {
//     if (!chatContainerRef.current) return;

//     const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
//     setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
//   }, []);

//   const scrollToBottom = useCallback(() => {
//     if (isAtBottom && chatContainerRef.current) {
//       chatContainerRef.current.scrollTop =
//         chatContainerRef.current.scrollHeight;
//     }
//   }, [isAtBottom]);

//   return { chatContainerRef, handleScroll, scrollToBottom };
// };

// // Utility Functions
// const formatDateOnly = (dateString) => {
//   const [datePart] = dateString.split(" ");
//   const [day, month, year] = datePart.split("-").map(Number);
//   return `${day.toString().padStart(2, "0")}-${month
//     .toString()
//     .padStart(2, "0")}-${year}`;
// };

// const formatTimeOnly = (timestamp) => {
//   const parts = timestamp.split(" ");
//   const timeParts = parts[1].split(":");
//   return `${timeParts[0]}:${timeParts[1]} ${parts[2]}`;
// };

// const isUserActive = (user) => {
//   if (!user?.activeTime) return false;
//   return (
//     new Date(user.activeTime).getTime() >= Date.now() - INACTIVITY_THRESHOLD
//   );
// };

// // Sub-components
// const SolvedUsersList = ({ collections, tenantName }) => {
//   if (collections.length === 0) {
//     return (
//       <div className="empty-state">
//         <i className="mdi mdi-check-all"></i>
//         <p>No solved conversations</p>
//       </div>
//     );
//   }

//   return collections.map((user) => (
//     <Link
//       key={user.email}
//       to={`/${tenantName}/chat/Conversation/Solved/${user.email}`}
//       className="user-item"
//     >
//       <div className={`user-avatar ${isUserActive(user) ? "active" : ""}`}>
//         {user.email.charAt(0).toUpperCase()}
//       </div>
//       <div className="user-info">
//         <span className="user-email">{user.email}</span>
//         {isUserActive(user) && (
//           <span className="user-status">Recently active</span>
//         )}
//       </div>
//       <i className="mdi mdi-chevron-right user-arrow"></i>
//     </Link>
//   ));
// };

// const ChatMessages = ({ messages }) => {
//   if (messages.length === 0) {
//     return (
//       <div className="empty-chat-state">
//         <i className="mdi mdi-check-all"></i>
//         <p>This conversation was resolved</p>
//         <small>Marked as solved</small>
//       </div>
//     );
//   }

//   return messages.map((msg, index) => {
//     const isDateChanged =
//       index === 0 ||
//       formatDateOnly(msg.time) !== formatDateOnly(messages[index - 1]?.time);

//     return (
//       <React.Fragment key={index}>
//         {isDateChanged && (
//           <div className="chat-date-divider">
//             <span>{formatDateOnly(msg.time)}</span>
//           </div>
//         )}
//         <div
//           className={`message-container ${msg.admin ? "outgoing" : "incoming"}`}
//         >
//           <div className={`message-bubble ${msg.likeStatus || ""}`}>
//             <div className="message-content">{msg.text}</div>
//             <div className="message-meta">
//               <span className="message-time">{formatTimeOnly(msg.time)}</span>
//               {msg.admin && msg.likeStatus && (
//                 <span className="message-reaction">
//                   {msg.likeStatus === "liked" ? (
//                     <i className="mdi mdi-thumb-up text-success"></i>
//                   ) : (
//                     <i className="mdi mdi-thumb-down text-danger"></i>
//                   )}
//                 </span>
//               )}
//             </div>
//           </div>
//         </div>
//       </React.Fragment>
//     );
//   });
// };

// const ChatActionBar = ({ selectedCollection, onReopenConversation }) => {
//   if (!selectedCollection) return null;

//   return (
//     <div className="chat-action-bar">
//       <button
//         className="reopen-conversation-btn"
//         onClick={onReopenConversation}
//       >
//         <i className="mdi mdi-archive-arrow-up"></i>
//         Reopen Conversation
//       </button>
//     </div>
//   );
// };

// const LoadingSpinner = () => (
//   <div className="loading-container">
//     <Spinner
//       animation="border"
//       variant="primary"
//       style={{ width: "3rem", height: "3rem" }}
//     />
//   </div>
// );

// // Main Component
// function SolvedNew() {
//   const navigate = useNavigate();
//   const { collection } = useParams();
//   const tenantName = localStorage.getItem("tenantName");

//   const [selectedCollection, setSelectedCollection] = useState("");

//   const { collections, loading: chatLoading } = useChatData(CHAT_STATUS.SOLVED);
//   const { messages, userDetails, fetchMessages } =
//     useChatMessages(selectedCollection);
//   const { chatContainerRef, handleScroll, scrollToBottom } =
//     useScrollManagement();

//   const handleCollectionClick = useCallback(
//     (collection) => {
//       setSelectedCollection(collection);
//       fetchMessages(collection);
//     },
//     [fetchMessages]
//   );

//   const handleStatusChange = useCallback(async () => {
//     if (!selectedCollection) return;

//     try {
//       const localURL = localStorage.getItem("shopURL");
//       const headers = {
//         "Content-Type": "application/json",
//         "x-shop-url": localURL,
//       };

//       await axios.post(
//         `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/status-update`,
//         { email: selectedCollection, status: CHAT_STATUS.MY_OPEN },
//         { headers }
//       );

//       navigate(`/${tenantName}/chat/Conversation/MyOpen/${selectedCollection}`);
//     } catch (error) {
//       console.error("Error updating status:", error);
//     }
//   }, [selectedCollection, tenantName, navigate]);

//   // Effects
//   useEffect(() => {
//     if (collection && collection !== ":collection") {
//       handleCollectionClick(collection);
//     }
//   }, [collection, handleCollectionClick]);

//   useEffect(() => {
//     scrollToBottom();
//   }, [messages, scrollToBottom]);

//   // Note: Polling is commented out in original, but here's how it would be implemented
//   // useEffect(() => {
//   //   if (!selectedCollection) return;
//   //
//   //   const interval = setInterval(() => {
//   //     fetchMessages(selectedCollection);
//   //   }, 5000);
//   //
//   //   return () => clearInterval(interval);
//   // }, [selectedCollection, fetchMessages]);

//   if (chatLoading) {
//     return (
//       <div className="d-flex flex-column w-100">
//         <Navbar />
//         <div className="d-flex w-100">
//           <NewSidebar />
//           <div
//             className="main-panel"
//             style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
//           >
//             <div className="loading-container">
//               <LoadingSpinner />
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="d-flex flex-column w-100">
//       <Navbar />
//       <div className="d-flex w-100">
//         <NewSidebar />
//         <div
//           className="main-panel"
//           style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
//         >
//           <div className="content-wrapper">
//             <div className="d-xl-flex justify-content-between align-items-start">
//               <h2 className="text-dark font-weight-bold mb-2">Conversation</h2>
//             </div>

//             <div className="row">
//               <div className="col-md-12">
//                 <div className="tab-content tab-transparent-content">
//                   <div
//                     className="tab-pane fade show active"
//                     id="business-1"
//                     role="tabpanel"
//                   >
//                     <div className="row">
//                       <div className="col-12 grid-margin">
//                         <div className="card">
//                           <div className="card-body">
//                             <div className="row d-flex flex-wrap">
//                               <InboxPanel />

//                               <div className="solved-users-panel col-md-3">
//                                 <div className="panel-header">
//                                   <h3 className="panel-title">
//                                     Solved Conversations
//                                   </h3>
//                                   <div className="panel-badge">
//                                     {collections.length}
//                                   </div>
//                                 </div>
//                                 <div className="users-list-container">
//                                   <SolvedUsersList
//                                     collections={collections}
//                                     tenantName={tenantName}
//                                   />
//                                 </div>
//                               </div>

//                               <div className="solved-chat-panel col-md-4">
//                                 <div className="chat-panel-header">
//                                   <h3 className="chat-title">
//                                     Solved Conversation
//                                   </h3>
//                                   <div className="chat-status-badge">
//                                     Completed
//                                   </div>
//                                 </div>
//                                 <div
//                                   className="chat-messages-container"
//                                   ref={chatContainerRef}
//                                   onScroll={handleScroll}
//                                 >
//                                   <ChatMessages messages={messages} />
//                                 </div>
//                                 <ChatActionBar
//                                   selectedCollection={selectedCollection}
//                                   onReopenConversation={handleStatusChange}
//                                 />
//                               </div>

//                               <div className="col-md-3 col-lg-3">
//                                 <div className="d-flex justify-content-between align-items-center mb-4">
//                                   <h4 className="card-title mb-0">
//                                     Information
//                                   </h4>
//                                 </div>
//                                 <div className="info-panel">
//                                   <UserDetails {...userDetails} />
//                                 </div>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default SolvedNew;
